import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

import type { PjazzaPendingTable, PjazzaTabSession } from '@/domains/pjazza/types/models/PjazzaTab.types.ts'

/**
 * The table a diner is sitting at, for as long as they are sitting at it.
 *
 * Held on the device rather than in a cookie because several people at one
 * table each scan the same printed slip on their own phone — there is no
 * single browser session to attach it to. The token is the whole
 * authorisation, so it is scoped per venue and cleared the moment the
 * server says the tab has closed.
 */
export const useTabStore = defineStore('pjazza-tab', () => {
  const session = useLocalStorage<PjazzaTabSession | null>('twn-pjazza-tab', null, {
    serializer: {
      read: (raw: string): PjazzaTabSession | null => {
        try {
          const parsed = JSON.parse(raw)

          return parsed?.siteSlug && parsed?.sessionToken ? (parsed as PjazzaTabSession) : null
        } catch {
          return null
        }
      },
      write: (value: PjazzaTabSession | null): string => JSON.stringify(value)
    },
    // Safari private mode throws on write; an in-memory tab still works.
    onError: () => undefined
  })

  /**
   * A table chosen in picker mode, before anything has been ordered onto it.
   *
   * Kept apart from `session` rather than folded into it: a session means a tab
   * exists server-side and this device holds the token that authorises it, and
   * that guard is worth keeping absolute. A pending table authorises nothing —
   * it is the answer to "where are you sitting", remembered so the diner is not
   * asked twice.
   */
  const pendingTable = useLocalStorage<PjazzaPendingTable | null>('twn-pjazza-pending-table', null, {
    serializer: {
      read: (raw: string): PjazzaPendingTable | null => {
        try {
          const parsed = JSON.parse(raw)

          return parsed?.siteSlug && parsed?.tableId ? (parsed as PjazzaPendingTable) : null
        } catch {
          return null
        }
      },
      write: (value: PjazzaPendingTable | null): string => JSON.stringify(value)
    },
    onError: () => undefined
  })

  const isActiveFor = (siteSlug: string): boolean => session.value?.siteSlug === siteSlug

  const tokenFor = (siteSlug: string): string | null =>
    isActiveFor(siteSlug) ? (session.value?.sessionToken ?? null) : null

  const pendingTableFor = (siteSlug: string): PjazzaPendingTable | null =>
    pendingTable.value?.siteSlug === siteSlug ? pendingTable.value : null

  /**
   * The table to show in the venue's chrome, tab or not.
   * Scoped to the active site so switching venues does not leak another venue's table.
   */
  const tableNameFor = (siteSlug?: string | null): string | null => {
    if (!siteSlug) return null
    if (session.value?.siteSlug === siteSlug) return session.value.tableName
    if (pendingTable.value?.siteSlug === siteSlug) return pendingTable.value.tableName
    return null
  }

  const hasOpenTabFor = (siteSlug?: string | null): boolean => {
    if (!siteSlug) return false
    return session.value?.siteSlug === siteSlug
  }

  const choose = (next: PjazzaPendingTable): void => {
    pendingTable.value = next
  }

  const join = (next: PjazzaTabSession): void => {
    session.value = next

    // The tab supersedes the guess that led to it.
    pendingTable.value = null
  }

  /** Called when the server reports the tab gone — settled, or timed out. */
  const leave = (): void => {
    session.value = null
    pendingTable.value = null
  }

  return {
    session,
    pendingTable,
    tableNameFor,
    hasOpenTabFor,
    isActiveFor,
    tokenFor,
    pendingTableFor,
    choose,
    join,
    leave
  }
})
